// src/routes/AppRoutes.js
import React, { Suspense, useRef } from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import Layout from "../layouts/Layout";

import SimpleTestPage from "../pages/SimpleTestPage";
import ContactUsPage from "../pages/ContactUsPage";
import AboutUsPage from "../pages/AboutUsPage";
import AuthCallbackPage from "../pages/AuthCallbackPage";
import WorkflowHistoryPage from "../pages/WorkflowHistoryPage";
import PendingTaskPage from "../pages/PendingTaskPage";
import UserCompaniesPage from "../pages/UserCompaniesPage";
import ManageUsersPage from "../pages/ManageUsersPage";
import LoginPage from "../pages/LoginPage";
//import WorkflowHistoryView from "components/workflow/WorkflowHistoryView";
import { useAuth } from "../hooks/useAuth";
import ProtectedRoute from "components/ProtectedRoute";
import WorkflowListPage from "pages/WorkflowListPage";
import UnassignedTaskPage from "pages/UnassignedTaskPage";
import ManageGeneratorsPage from "pages/ManageGeneratorsPage";
import ManageContactsPage from "pages/ManageContactsPage";
import ManageCompanyContactsPage from "pages/ManageCompanyContactsPage";
import ManageTrucksPage from "pages/ManageTrucksPage";
import ManageTemplatesPage from "pages/ManageTemplatesPage";
import ManageBranchesPage from "pages/ManageBranchesPage";
import DynamicServicePage from "pages/DynamicServicePage";
import { logger } from "helpers/logger";
import UserManualsPage from "pages/UserManualsPage";
//import FormViewRenderer from "components/forms/FormViewRenderer";

//import ManageForms from "components/forms/ManageForms";
//import EditForm from "components/forms/EditForm";
//import FormViewer from "components/forms/FormViewer"
// Bootstrap's and Form.io's stylesheets stay in the main bundle, imported at the same spot as
// before (CSS order decides which rule wins): Bootstrap's base styles apply to every page, not only
// the forms, so they must not wait for the lazy Form.io code below.
import "bootstrap/dist/css/bootstrap.min.css";
import "@formio/js/dist/formio.full.min.css";
import "components/formio/formioTheme.css";
import ImportDataPage from "pages/ImportDataPage";
import ImportWizardPage from "pages/ImportWizardPage";
import DashboardPage from "pages/DashboardPage";
import DashboardGridPage from "pages/DashboardGridPage";
import Loader from "components/common/custom-component/Loader";
import lazyWithReload from "helpers/lazyWithReload";

// Form.io (~1.8 MB with its dependencies) is only needed by the form pages, so they and the provider
// load on demand: the first time one of them is opened the browser downloads this part, then it's
// cached (and the service worker precaches it in the background, so later visits and offline use
// don't wait). lazyWithReload: a page left open across a deploy reloads instead of failing.
const FormioProvider = lazyWithReload(() =>
	import(/* webpackChunkName: "formio" */ "components/formio/FormioProvider").then((m) => ({ default: m.FormioProvider }))
);
const ManageFormsPage = lazyWithReload(() => import(/* webpackChunkName: "formio-pages" */ "pages/ManageFormsPage"));
const FormRendererPage = lazyWithReload(() => import(/* webpackChunkName: "formio-pages" */ "pages/FormRendererPage"));

// Shares a single FormioProvider (and its Form.io setup/Bootstrap JS) across every nested Form.io
// route, instead of mounting it app-wide or repeating it on each route. The loader shows while the
// Form.io part downloads (first visit only).
const FormioProviderLayout = () => (
	<Suspense
		fallback={
			<div style={{ display: "flex", justifyContent: "center", padding: "64px 0" }}>
				<Loader variant="dots" message="Loading." />
			</div>
		}
	>
		<FormioProvider>
			<Outlet />
		</FormioProvider>
	</Suspense>
);

export default function AppRoutes({ loginKeycloakIframeFlag }) {
	const appRoutesId = useRef(Math.random().toString(36).substring(7));
	logger.info(`📍 AppRoutes ${appRoutesId.current} rendered`, {
		loginKeycloakIframe: loginKeycloakIframeFlag,
	});

	const keycloakAuth = useAuth();
	const isInIframe = window.self !== window.top;

	// Show loading while Keycloak initializes
	if (keycloakAuth.isLoading) {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
				}}
			>
				Loading...
			</div>
		);
	}

	// If inside silent SSO iframe, only render /auth/callback
	/* if (isInIframe) {
		console.log('🖼️ Running inside iframe - no Layout');
		return (
			<Routes>
				<Route path="/auth/callback" element={<AuthCallbackPage />} />
			</Routes>
		);
	} */

	// Main window SPA routes
	return (
		<Routes>
			{/* Routes WITH Layout */}
			<Route element={<Layout />}>
				<Route
					path="/"
					element={
						<ProtectedRoute>
							<Navigate to="/login" replace />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/test"
					element={
						<ProtectedRoute>
							<SimpleTestPage />
						</ProtectedRoute>
					}
				/>

				<Route
					path="/workflowHistory"
					element={
						<ProtectedRoute>
							<WorkflowHistoryPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/workflowList"
					element={
						<ProtectedRoute>
							<WorkflowListPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/userTasks"
					element={
						<ProtectedRoute>
							<PendingTaskPage />
						</ProtectedRoute>
					}
				/>

				<Route
					path="/unassignedTasks"
					element={
						<ProtectedRoute>
							<UnassignedTaskPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/userCompanies"
					element={
						<ProtectedRoute authOnly>
							<UserCompaniesPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/manageUsers"
					element={
						<ProtectedRoute>
							<ManageUsersPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/manageGenerators"
					element={
						<ProtectedRoute>
							<ManageGeneratorsPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/manageContacts"
					element={
						<ProtectedRoute>
							<ManageContactsPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/manage-contacts/:selectedCompanyId?"
					element={
						<ProtectedRoute>
							<ManageContactsPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/manageCompanyContacts"
					element={
						<ProtectedRoute>
							<ManageCompanyContactsPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/manageFleet"
					element={
						<ProtectedRoute>
							<ManageTrucksPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/manageTemplates"
					element={
						<ProtectedRoute>
							<ManageTemplatesPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/manageBranch"
					element={
						<ProtectedRoute>
							<ManageBranchesPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/import-data"
					element={
						<ProtectedRoute>
							<ImportDataPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/import/:importTypeKey"
					element={
						<ProtectedRoute>
							<ImportWizardPage />
						</ProtectedRoute>
					}
				/>
				<Route element={<FormioProviderLayout />}>
					<Route
						path="/manageForms"
						element={
							<ProtectedRoute>
								<ManageFormsPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/formRenderer"
						element={
							<ProtectedRoute>
								<FormRendererPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/form_view_renderer"
						element={
							<ProtectedRoute>
								<FormRendererPage />
							</ProtectedRoute>
						}
					/>
					{/* <Route
						path="/form_view_renderer"
						element={
							<ProtectedRoute>
								<FormViewRenderer />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/formedit/:id"
						element={
							<ProtectedRoute>
								<EditForm />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/formView/:id"
						element={
							<ProtectedRoute>
								<FormViewer />
							</ProtectedRoute>
						}
					/> */}
				</Route>
				<Route
					path="/dashboards"
					element={
						<ProtectedRoute>
							<DashboardGridPage />
						</ProtectedRoute>
					}
				/>
				{/* Unproteted Routes */}
				<Route path="/services/:serviceName" element={<DynamicServicePage />} />
				<Route path="/manuals" element={<UserManualsPage />} />
				{/* <Route path="/workflow-history-view" element={<WorkflowHistoryView />} /> */}
				<Route path="/login" element={<LoginPage loginKeycloakIframeFlag={loginKeycloakIframeFlag} />} />
				<Route path="/contact" element={<ContactUsPage />} />
				<Route path="/aboutus" element={<AboutUsPage />} />
			</Route>

			{/* Routes WITHOUT Layout */}
			{/* <Route path="/login" element={<LoginPage loginKeycloakIframeFlag={loginKeycloakIframeFlag} />} /> */}
			<Route path="/auth/callback" element={<AuthCallbackPage />} />
		</Routes>
	);
}
